<template>
    <AdaptiveModal v-model="createEventPopup" @after-leave="onReset" :title="modalTitle">
        <div class="modal-body-content p-3 adaptive-modal-size-medium">
            <b-form-group id="example-input-group-1" label-for="example-input-1" class="pb-3">
                <b-form-input id="input-2" v-model="viewModel.createEventData.eventName" placeholder="Event Name" :state="eventNameValid == null ? null : eventNameValid ? null : false" @input="validate()" @hidden="validate()"></b-form-input>
                <p v-if="eventNameValid == false" class="text-start text-error createEdit ps-2">Enter 1 or more characters</p>
            </b-form-group>
            <b-form-textarea id="input-3" :rows="isMobile ? 5 : 3" max-rows="5" v-model="viewModel.createEventData.description" placeholder="Event description"></b-form-textarea>
            <b-row class="mt-3 mb-3 justify-content-between">
                <b-col>
                    <BFormInput type="date" :state="startDateValid" :placeholder="'Start date'" @input="validate()" @hidden="validate()" v-model="viewModel.createEventData.startDate" class="mb-1 dateTime"></BFormInput>
                    <p v-if="startDateValid == false" class="text-start text-error createEdit">Enter a valid start date</p>
                </b-col>
                <b-col>
                    <BFormInput type="time" :state="startTimeValid" :placeholder="'Start time'" @input="validate()" @hidden="validate()" v-model="viewModel.createEventData.startTime" class="mb-1"></BFormInput>
                    <p v-if="startTimeValid == false" class="text-start text-error createEdit">Enter a valid start time</p>
                </b-col>
            </b-row>
            <div v-if="viewModel.createEventData.endDateEnabled"></div>
            <b-row v-if="viewModel.createEventData.endDateEnabled">
                <b-col>
                    <BFormInput type="date" :state="endDateValid" :placeholder="'Start date'" @input="validate()" @hidden="validate()" v-model="viewModel.createEventData.endDate" class="mb-1 dateTime"></BFormInput>
                </b-col>
                <b-col>
                    <BFormInput type="time" :state="endDateValid ? null : false" :placeholder="'End time'" @input="validate()" @hidden="validate()" v-model="viewModel.createEventData.endTime" class="mb-1"></BFormInput>
                </b-col>
            </b-row>
            <div v-if="viewModel.createEventData.endDateEnabled && !endDateValid" class="text-start text-error createEdit ps-2 pb-2">Ensure end date is after start date</div>
            <b-link v-if="viewModel.createEventData.endDateEnabled" @click="toggleEndDate()" enabled>- End Date and Time</b-link>
            <b-link v-else @click="toggleEndDate()" enabled class="p-2">+ End Date and Time</b-link>
            <div style="margin-top: 20px"></div>
            <b-form-input id="input-4" v-model="viewModel.createEventData.location" placeholder="Event Location"></b-form-input>
        </div>
        <AdaptiveModalFooter @cancel="createEventPopup = false" @save="onSubmitWrapper" :is-save-disabled="!eventValid" :is-cancel-visible="false" />
    </AdaptiveModal>
</template>

<script lang="ts">
import EventCreationViewModel from "@/EventCreationViewModel";
import { defineComponent, ref, reactive } from "vue";
import { inject } from "vue";
import Api from "@/Api";
import AuthManager from "@/AuthManager";
import { useToast } from "vue-toastification";
import { useRouter } from "vue-router";
import EventViewModel from "@/EventViewModel";
import AdaptiveModal from "@/components/AdaptiveModal.vue";
import AdaptiveModalFooter from "@/components/AdaptiveModalFooter.vue";
import { mobileUtil } from "@/util/MobileUtil";

export enum CreationMode {
    create,
    edit,
    onboarding,
}

interface GlobalRefs {
    loginPopUpUi: {
        showLoginUi: () => void;
    };
}

export default defineComponent({
    name: "CreateEvent",
    components: {
        AdaptiveModal,
        AdaptiveModalFooter,
    },
    setup() {
        const format = "dd-mm-yyyy";

        const globalRefs = inject<GlobalRefs>("globalRefs");
        if (!globalRefs) {
            throw new Error("globalRefs not provided");
        }

        const api = inject<Api>("api");
        if (!api) {
            throw new Error("API not provided");
        }
        const authManager = inject<AuthManager>("authManager");
        if (!authManager) {
            throw new Error("AuthManager not provided");
        }
        const toast = useToast();
        const router = useRouter();
        const createEventPopup = ref(false);

        const startDate = ref(new Date());
        const endDate = ref(new Date());

        // Create the viewModel in setup to ensure proper typing
        const viewModel = reactive(
            new EventCreationViewModel(authManager, api, (error: any) => {
                console.error(error);
                if (error.response) {
                    toast.error(error.response.data.reason);
                }
            })
        );

        const { isMobile } = mobileUtil();

        return {
            globalRefs,
            api,
            authManager,
            toast,
            router,
            createEventPopup,
            startDate,
            endDate,
            viewModel,
            isMobile,
        };
    },
    data() {
        return {
            mode: CreationMode.create,
            eventNameValid: null as boolean | null,
            eventValid: false,
            startDateValid: null as boolean | null,
            startTimeValid: null as boolean | null,
            endDateValid: null as boolean | null,
            eventViewModel: null as EventViewModel | null,
        };
    },
    computed: {
        modalTitle(): string {
            if (this.mode === CreationMode.create || this.mode === CreationMode.onboarding) {
                return "Create Event";
            } else {
                return "Edit Event";
            }
        },
    },
    async mounted() {
        // this.viewModel.reset()
    },
    methods: {
        showUi() {
            this.viewModel.reset();
            this.mode = CreationMode.create;
            this.createEventPopup = true;
        },
        showEditUi(eventViewModel: EventViewModel) {
            if (eventViewModel.event == null) {
                return;
            }
            this.eventViewModel = eventViewModel;
            this.mode = CreationMode.edit;
            this.viewModel.setEventData(eventViewModel.event);
            this.validate();
            this.createEventPopup = true;
        },
        showOnboardingUi() {
            this.viewModel.reset();
            this.mode = CreationMode.onboarding;
            this.createEventPopup = true;
        },
        handleClose() {
            this.createEventPopup = false;
        },
        onSubmitWrapper() {
            // The AdaptiveModalFooter expects a simple call,
            // b-modal's @ok passes a BvModalEvent. We adapt here.
            this.onSubmit({ preventDefault: () => {} });
        },
        toggleEndDate() {
            this.viewModel.createEventData.endDateEnabled = !this.viewModel.createEventData.endDateEnabled;

            if (this.viewModel.createEventData.endDateEnabled) {
                this.viewModel.setReasonableEndDate();
            }
            this.validate();
        },
        async onSubmit(bvModalEvent: { preventDefault: () => void }) {
            if (!this.validate()) {
                this.toast.error("Invalid event");
                bvModalEvent.preventDefault();
                return;
            }

            if (this.mode == CreationMode.onboarding) {
                // open login popup
                localStorage.createEventData = this.viewModel.createEventData.toJson();
                this.globalRefs.loginPopUpUi.showLoginUi();
                return;
            }

            if (this.mode == CreationMode.edit) {
                if (this.viewModel.createEventData.event == null) {
                    console.error("Event is null in vm");
                    this.toast.error("Event does not exist");
                    return;
                }
                const updatedEvent = await this.viewModel.update();
                if (this.eventViewModel && updatedEvent) {
                    this.eventViewModel.setEvent(updatedEvent);
                }
            } else {
                const createdEventId = await this.viewModel.create();
                window.location.href = "/e/" + createdEventId;
            }
            this.createEventPopup = false;
        },
        async onSubmitAfterOnboarding() {
            if (!this.validate()) {
                this.toast.error("Invalid event");
                return;
            }

            const createdEventId = await this.viewModel.create();
            this.router.push({ name: "Event", params: { id: createdEventId } });
        },
        onReset() {
            this.viewModel.reset();
            this.eventNameValid = null;
            this.eventValid = false;
            this.startDateValid = null;
            this.startTimeValid = null;
            this.endDateValid = null;
        },
        handleError(error: any) {
            console.error(error);
            if (error.response) {
                this.toast.error(error.response.data.reason);
            }
        },
        validateEventName() {
            this.eventNameValid = this.viewModel.createEventData.eventName.length > 0;
            return this.eventNameValid;
        },
        validateDateTime() {
            const endD = this.viewModel.createEventData.enddatetime();
            const startD = this.viewModel.createEventData.strtdatetime();

            // Validate start date and time are set
            if (!this.viewModel.createEventData.startDate || !this.viewModel.createEventData.startTime) {
                this.startDateValid = false;
                this.startTimeValid = false;
                return false;
            }

            if (!this.viewModel.createEventData.endDateEnabled) {
                return true;
            }

            if (endD > startD && this.viewModel.createEventData.endTime != "") {
                this.endDateValid = true;
            } else {
                this.endDateValid = false;
                return false;
            }
            return true;
        },
        validate() {
            if (!this.validateDateTime()) {
                this.eventValid = false;
                return false;
            }
            if (!this.validateEventName()) {
                this.eventValid = false;
                return false;
            }

            if (this.viewModel.createEventData.endDateEnabled && this.eventNameValid && this.endDateValid) {
                this.eventValid = true;
                return true;
            } else if (this.eventNameValid) {
                this.eventValid = true;
                return true;
            }
            this.eventValid = false;
            return false;
        },
    },
});
</script>

<style scoped>
.text-error {
    color: rgb(255, 9, 9);
}

:deep(.form-control) {
    background-color: var(--input-bg);
    color: var(--text-primary);
    border-color: var(--input-border);
}

:deep(.form-control:focus) {
    background-color: var(--input-bg);
    color: var(--text-primary);
    border-color: var(--input-border);
}

:deep(.form-control::placeholder) {
    color: var(--text-secondary);
}
</style>
